import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
	IAuPaginationData,
	IDjangoPaginationData,
} from '@shared/ui-components/au-paginator/pagination-page.info';
import {
	ICmmsIncidentsQueryVariables,
	IIncident,
} from '@app/pages/cmms/interfaces/incidents.interface';
import {
	addIncident,
	addIncidentExternalData,
	clearIncidentExternalData,
	deleteIncident,
	deleteIncidents,
	dropIncidentsQueryVariables,
	incidentsFailedToLoad,
	incidentsLoaded,
	openNewIncidentWorkspaceFromOutside,
	setIncident,
	setIncidentForEditing,
	setIncidents,
	setIncidentsSelectedCalendarDate,
	setIncidentsTableConfig,
	startLoadingIncidents,
	storeIncidentsQueryVariables,
	updateIncident,
} from './incidents.actions';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import { DocumentNode } from 'graphql';
import { IncidentsQuery } from '@app/pages/cmms/graphql/incident.queries';

export interface IIncidentState extends EntityState<IIncident> {
	// additional entity state properties
	incidentPagination: IDjangoPaginationData | null;
	incidentAuPagination: IAuPaginationData | null;
	incidentsQueryVariables: ICmmsIncidentsQueryVariables | null;
	incidentEdit: IIncident;
	incidentsIsLoading: boolean;
	incidentData?: Partial<IIncident>;
	isExportable: boolean;
	tableColumnsConfig: ICMMSColumn[] | null;
	exportUrlQuery: DocumentNode | null;
	selectedCalendarDate: string | null;
}

export function selectIncidentId(item: IIncident): string {
	return item.pk;
}

export const IncidentAdapter: EntityAdapter<IIncident> = createEntityAdapter<IIncident>({
	selectId: selectIncidentId,
});

export const initialState: IIncidentState = IncidentAdapter.getInitialState({
	// additional entity state properties
	incidentPagination: null,
	incidentAuPagination: null,
	incidentsQueryVariables: null,
	incidentEdit: null,
	incidentsIsLoading: true,
	incidentData: null,
	isExportable: false,
	tableColumnsConfig: null,
	exportUrlQuery: structuredClone(IncidentsQuery),
	selectedCalendarDate: null,
});

export const incidentReducer = createReducer(
	initialState,
	on(addIncident, (state, { incident }) => {
		return IncidentAdapter.addOne(incident, state);
	}),
	on(setIncident, (state, { incident }) => {
		return IncidentAdapter.setOne(incident, state);
	}),
	on(updateIncident, (state, { update }) => {
		return IncidentAdapter.updateOne(update, {
			...state,
			incidentEdit: update.changes as IIncident,
		});
	}),
	on(deleteIncident, (state, { pk }) => {
		return IncidentAdapter.removeOne(pk, state);
	}),
	on(deleteIncidents, (state, { pks }) => {
		return IncidentAdapter.removeMany(pks, state);
	}),
	on(startLoadingIncidents, (state, { variables }) => {
		return {
			...state,
			incidentAuPagination: {
				first: variables?.first,
				last: variables?.last,
				after: variables?.after,
				before: variables?.before,
			},
			incidentsIsLoading: true,
		};
	}),
	on(incidentsLoaded, (state, { incidents, incidentPagination }) => {
		return IncidentAdapter.setAll(incidents, {
			...state,
			incidentPagination: incidentPagination,
			incidentsIsLoading: false,
		});
	}),
	on(incidentsFailedToLoad, state => {
		return IncidentAdapter.setAll([], {
			...state,
			incidentsIsLoading: false,
		});
	}),
	on(setIncidentForEditing, (state, { incidentEdit }) => {
		return { ...state, incidentEdit };
	}),
	on(setIncidents, (state, { incidents }) => {
		return IncidentAdapter.setMany(incidents, state);
	}),
	on(openNewIncidentWorkspaceFromOutside, (state, { incidentData }) => {
		return { ...state, incidentData };
	}),
	on(storeIncidentsQueryVariables, (state, { variables }) => {
		return { ...state, incidentsQueryVariables: variables };
	}),
	on(dropIncidentsQueryVariables, state => {
		return { ...state, incidentsQueryVariables: null };
	}),
	on(setIncidentsTableConfig, (state, { isExportable, columns }) => {
		return {
			...state,
			isExportable,
			tableColumnsConfig: columns,
		};
	}),
	on(setIncidentsSelectedCalendarDate, (state, { selectedDate }) => {
		return {
			...state,
			selectedCalendarDate: selectedDate,
		};
	}),
	on(addIncidentExternalData, (state, { incidentData }) => {
		return {
			...state,
			incidentData,
		};
	}),
	on(clearIncidentExternalData, state => {
		return {
			...state,
			incidentData: null,
		};
	})
);

const { selectIds, selectEntities, selectAll, selectTotal } =
	IncidentAdapter.getSelectors();

export const IncidentEntitySelector = {
	selectIds,
	selectEntities,
	selectAll,
	selectTotal,
};
